import storage from './storage';

/**
 * Activities are stored as { [leadId]: Activity[] } under a single global key
 * `crm_activities` (same for all Nutzer im Team / dieselbe Browser-Instanz).
 * Lead-IDs kommen aus den Kontakten — Zuordnung erfolgt immer über normLeadId().
 */
const ACTIVITIES_KEY = 'crm_activities';

// In-memory cache — avoids repeated localStorage.getItem + JSON.parse on every render
let _cache = null;
function invalidateCache() { _cache = null; }
// Invalidate when other tabs/cloud writes change localStorage
window.addEventListener('crm-activities-changed', invalidateCache);
window.addEventListener('crm-team-data-changed', invalidateCache);

function normLeadId(leadId) {
  return String(leadId);
}

export function actTime(act) {
  const t = act?.ts || act?.timestamp;
  if (t) {
    const ms = Date.parse(t);
    if (Number.isFinite(ms)) return ms;
  }
  const d = act?.date;
  if (d && typeof d === 'string') {
    const p = d.trim().split('.');
    if (p.length === 3) {
      const ms = new Date(+p[2], +p[1] - 1, +p[0]).getTime();
      if (Number.isFinite(ms)) return ms;
    }
  }
  const idn = Number(act?.id);
  return Number.isFinite(idn) ? idn : 0;
}

/** Neuestes Aktivitäts-Datum (ms) aus der Timeline, oder null. */
export function latestActivityTimestampMs(activities) {
  if (!Array.isArray(activities) || activities.length === 0) return null;
  let best = 0;
  for (const act of activities) {
    const t = actTime(act);
    if (t > best) best = t;
  }
  return best > 0 ? best : null;
}

/** Für `lead.activity` (de-DE Kurzdatum wie bisher im CRM). */
export function latestActivityDateDe(activities) {
  const ms = latestActivityTimestampMs(activities);
  if (ms == null) return null;
  return new Date(ms).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Vereinigt zwei Aktivitätslisten pro Lead (gleiche id → neuere Version gewinnt). */
export function mergeActivityLists(a = [], b = []) {
  const byId = new Map();
  for (const act of [...(Array.isArray(a) ? a : []), ...(Array.isArray(b) ? b : [])]) {
    if (!act || act.id == null) continue;
    const prev = byId.get(act.id);
    if (!prev || actTime(act) >= actTime(prev)) byId.set(act.id, act);
  }
  return Array.from(byId.values()).sort((x, y) => actTime(y) - actTime(x));
}

/** Vereinigt komplette { leadId: Activity[] }-Maps (z. B. lokal + Cloud). */
export function mergeActivityMaps(local = {}, remote = {}) {
  const out = {};
  function ingest(src) {
    if (!src || typeof src !== 'object' || Array.isArray(src)) return;
    for (const [k, v] of Object.entries(src)) {
      const nk = normLeadId(k);
      if (!Array.isArray(v)) continue;
      out[nk] = out[nk] ? mergeActivityLists(out[nk], v) : [...v];
    }
  }
  ingest(local);
  ingest(remote);
  return out;
}

function notifyActivitiesChanged(leadId) {
  try {
    window.dispatchEvent(new CustomEvent('crm-activities-changed', { detail: { leadId: normLeadId(leadId) } }));
  } catch { /* ignore */ }
}

export const activityService = {
  /** Returns { [leadId]: Activity[] } with normalized string keys. Cached until any write. */
  getAll() {
    if (_cache !== null) return _cache;
    const raw = storage.get(ACTIVITIES_KEY, {});
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) { _cache = {}; return _cache; }
    const out = {};
    for (const [k, v] of Object.entries(raw)) {
      const nk = normLeadId(k);
      if (!Array.isArray(v)) continue;
      out[nk] = out[nk] ? mergeActivityLists(out[nk], v) : [...v];
    }
    _cache = out;
    return _cache;
  },

  /** Returns Activity[] for a single lead. */
  getForLead(leadId) {
    const all = this.getAll();
    const nk = normLeadId(leadId);
    return all[nk] || [];
  },

  /** Saves all activities for a lead, replaces existing. */
  saveForLead(leadId, activities) {
    invalidateCache();
    const all = this.getAll();
    const nk = normLeadId(leadId);
    storage.setLocal(ACTIVITIES_KEY, { ...all, [nk]: Array.isArray(activities) ? activities : [] });
    notifyActivitiesChanged(nk);
    void import('./leadActivitiesCloud')
      .then(mod => mod.syncLeadRowToTeamCloud(nk, activities))
      .catch(err => {
        try {
          window.dispatchEvent(new CustomEvent('crm-error', {
            detail: {
              code: 'TEAM_ACT_IMPORT',
              message: 'Team-Cloud-Modul für Aktivitäten konnte nicht geladen werden.',
              detail: String(err?.message || err),
            },
          }));
        } catch { /* ignore */ }
      });
  },

  /** Adds a single activity to a lead's list (normalizes leadId + ts). */
  addToLead(leadId, activity) {
    const nk = normLeadId(leadId);
    const ms = actTime(activity);
    const normalized = {
      ...activity,
      leadId: activity.leadId != null ? normLeadId(activity.leadId) : nk,
      ts: activity.ts || (ms > 0 ? new Date(ms).toISOString() : new Date().toISOString()),
    };
    const current = this.getForLead(nk);
    this.saveForLead(nk, [normalized, ...current]);
  },

  /**
   * Duplikat-Merge: alle Aktivitäten von `sourceLeadId` in `targetLeadId` übernehmen,
   * Quell-Lead aus der Aktivitäts-Map entfernen.
   */
  mergeLeadsInto(targetLeadId, sourceLeadId) {
    invalidateCache();
    const nk = normLeadId(targetLeadId);
    const sk = normLeadId(sourceLeadId);
    const merged = mergeActivityLists(this.getForLead(nk), this.getForLead(sk));
    const all = { ...this.getAll() };
    all[nk] = merged;
    delete all[sk];
    storage.setLocal(ACTIVITIES_KEY, all);
    notifyActivitiesChanged(nk);
    notifyActivitiesChanged(sk);
    void import('./leadActivitiesCloud')
      .then(mod => {
        mod.syncLeadRowToTeamCloud(nk, merged);
        return mod.deleteTeamActivityRow(sk);
      })
      .catch(() => {});
  },

  /** Returns a flat array of all activities across all leads (each row includes leadId + ts). */
  getAllFlat() {
    const all = this.getAll();
    const flat = [];
    for (const [leadId, acts] of Object.entries(all)) {
      if (!Array.isArray(acts)) continue;
      const nk = normLeadId(leadId);
      for (const act of acts) {
        if (!act) continue;
        const ms = actTime(act);
        flat.push({
          ...act,
          leadId: act.leadId != null ? normLeadId(act.leadId) : nk,
          ts: act.ts || (ms > 0 ? new Date(ms).toISOString() : undefined),
        });
      }
    }
    return flat;
  },
};
